/**
 * Origin-match helper for AI deep links.
 *
 * AI markdown responses contain full HTTPS links built by Panther tools (e.g.
 * `[Alert xyz](https://customer.panther.io/alerts-and-errors/xyz/)`). Real URLs
 * work natively for every non-Console consumer (Slack, email, REST, MCP). Inside
 * the Console we use this helper to decide which links should render as styled
 * in-product `PantherDeepLink`s versus the generic external/disabled link path.
 *
 * Origin comparison (not scheme matching or URL-shape parsing) is intentional:
 * any future in-app URL shape works automatically without changes here.
 */

/** True when `href` resolves to the same origin as the current Console window. */
export function isSameOriginAsConsole(href: string | undefined | null): boolean {
  if (!href) {
    return false;
  }
  // Guard the window existence check BEFORE any property access — touching
  // window.location.origin first would throw in SSR/test environments that
  // don't provide a window global. Not a current concern for this app but
  // keeps the function defensively correct.
  if (typeof window === 'undefined' || !window.location?.origin) {
    return false;
  }
  // Dev affordance: when the app is running on localhost (e.g. `pnpm start`
  // pointed at a staging backend), treat any panther-domain URL as same-origin
  // so clicks route through the in-product styled-link path. Loose `includes`
  // match accommodates the multiple tenant domains we ship under (e.g. .panther.io,
  // .panther.com, etc.). In production the strict origin check below
  // is what gates same-origin
  if (window.location.origin.includes('localhost')) {
    try {
      const host = new URL(href, window.location.origin).hostname;
      if (host.includes('docs.panther.com') || host.includes('knowledge-base.help.usepylon.com')) {
        return false;
      }
      if (host.includes('panther') || host.includes('elb.amazonaws')) {
        return true;
      }
    } catch {
      return false;
    }
  }
  try {
    return new URL(href, window.location.origin).origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Returns pathname + search + hash of `href`, suitable for React Router's
 * `<Link to>`. Strips the origin so the caller doesn't need to parse it. Returns
 * an empty string for malformed input. Assumes the caller has already verified
 * same-origin via `isSameOriginAsConsole`.
 */
export function pathOf(href: string): string {
  try {
    const url = new URL(href, window.location.origin);
    return url.pathname + url.search + url.hash;
  } catch {
    return '';
  }
}
